import { useCallback, useEffect, useRef, useState } from "react";

// Fires once when the target element crosses into the viewport.
// Returns a CALLBACK ref to attach to the container + a `revealed` flag.
// Pair with the `reveal-pending` → `reveal-active` className swap and child
// `.reveal-row` / `.reveal-card` / `.reveal-bar` classes from index.css.
//
// Why callback ref (not useRef + useEffect):
//   useEffect runs once at mount. If the target element is conditionally
//   rendered (e.g. only when data has arrived), `ref.current` is null on
//   first effect run and the observer never gets set up — useEffect won't
//   re-run when the ref attaches later. Callback refs run on attach AND
//   detach, so we hook setup/teardown to the actual DOM lifecycle.
//
// Defensive design:
// - Defers the in-view check by one rAF so layout has settled before measuring.
// - Safety timeout (1500ms) flips revealed=true even if IO never fires, so
//   silent observer failures degrade to "no animation" not "blank rows."
// - SSR-safe: bails out cleanly when IntersectionObserver isn't available.
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  threshold = 0.12,
  rootMargin = "0px 0px -8% 0px"
) {
  const [revealed, setRevealed] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback(
    (el: T | null) => {
      // Detach: tear down any prior observer/timer/rAF when ref points
      // away from an element (component unmount or conditional swap).
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!el) return;
      if (typeof IntersectionObserver === "undefined") {
        setRevealed(true);
        return;
      }

      let cancelled = false;
      let obs: IntersectionObserver | null = null;
      let safetyTimer: number | null = null;

      const rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < vh * (1 - threshold) && rect.bottom > 0) {
          setRevealed(true);
          return;
        }
        obs = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                setRevealed(true);
                obs?.disconnect();
                break;
              }
            }
          },
          { threshold, rootMargin }
        );
        obs.observe(el);
        // Failsafe: if the observer never fires (zero-height-at-mount race,
        // overflow-clipped ancestor, etc.), reveal after 1.5s anyway.
        safetyTimer = window.setTimeout(() => setRevealed(true), 1500);
      });

      cleanupRef.current = () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
        if (safetyTimer !== null) window.clearTimeout(safetyTimer);
        obs?.disconnect();
      };
    },
    [threshold, rootMargin]
  );

  // Final teardown on unmount.
  useEffect(() => () => cleanupRef.current?.(), []);

  return { ref, revealed };
}
